import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  {
    name: "Services",
    path: "/services",
    dropdown: [
    { name: "Application Development", path: "/services/application-development" },
    { name: "Web Development", path: "/services/web-development" },
    { name: "Cryptocurrency Development", path: "/services/cryptocurrency-development" },
    { name: "IT Consultancy", path: "/services/it-consultancy" }]

  },
  { name: "Contact", path: "/contact" }];


  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ?
      "bg-[#030712]/90 backdrop-blur-xl border-b border-white/10 header-scrolled" :
      "bg-transparent"}`
      }
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="header">

      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_gwit-landing/artifacts/c7ifyjsg_WhatsApp%20Image%202026-03-13%20at%202.03.49%20PM.jpeg"
              alt="GWIT Logo"
              className="h-12 w-auto !shadow-sm" />

            <span className="hidden sm:block !font-extrabold !text-xl text-white">GWIT</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) =>
            <div key={link.name} className="relative group">
                {link.dropdown ?
              <div
                className="flex items-center gap-1 cursor-pointer"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}>

                    <Link
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                  location.pathname.startsWith(link.path) ?
                  "text-blue-400" :
                  "text-slate-300 hover:text-white"}`
                  }
                  data-testid={`nav-${link.name.toLowerCase()}`}>

                      {link.name}
                    </Link>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                    
                    <AnimatePresence>
                      {isServicesOpen &&
                  <motion.div
                    className="absolute top-full left-0 mt-2 w-64 py-2 glass rounded-xl"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}>

                          {link.dropdown.map((item) =>
                    <Link
                      key={item.name}
                      to={item.path}
                      className="block px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                      data-testid={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>

                              {item.name}
                            </Link>
                    )}
                        </motion.div>
                  }
                    </AnimatePresence>
                  </div> :

              <Link
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                location.pathname === link.path ?
                "text-blue-400" :
                "text-slate-300 hover:text-white"}`
                }
                data-testid={`nav-${link.name.toLowerCase()}`}>

                    {link.name}
                  </Link>
              }
              </div>
            )}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/contact">
              <Button
                className="bg-blue-600 text-white rounded-full px-6 py-2 font-semibold hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all"
                data-testid="header-cta-btn">

                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn">

            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen &&
        <motion.div
          className="lg:hidden absolute top-20 left-0 right-0 glass border-t border-white/10"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="mobile-menu">

            <nav className="px-6 py-4 space-y-4">
              {navLinks.map((link) =>
            <div key={link.name}>
                  <Link
                to={link.path}
                className={`block py-2 text-lg font-medium ${
                location.pathname === link.path ?
                "text-blue-400" :
                "text-slate-300"}`
                }
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}>

                    {link.name}
                  </Link>
                  {link.dropdown &&
              <div className="pl-4 space-y-2 mt-2">
                      {link.dropdown.map((item) =>
                <Link
                  key={item.name}
                  to={item.path}
                  className="block py-1 text-sm text-slate-400 hover:text-white">

                          {item.name}
                        </Link>
                )}
                    </div>
              }
                </div>
            )}
              <Link to="/contact" className="block pt-4">
                <Button
                className="w-full bg-blue-600 text-white rounded-full py-3 font-semibold"
                data-testid="mobile-cta-btn">

                  Get Started
                </Button>
              </Link>
            </nav>
          </motion.div>
        }
      </AnimatePresence>
    </motion.header>);

};

export default Header;